import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/auth`;
const STORAGE_KEY = "duds.auth.token";

const formatDetail = (detail) => {
  if (detail == null) return null;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

const loadToken = () => {
  try { return localStorage.getItem(STORAGE_KEY) || null; }
  catch { return null; }
};

const saveToken = (t) => {
  try {
    if (t) localStorage.setItem(STORAGE_KEY, t);
    else localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    console.warn("[auth] token persist failed:", e);
  }
};

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => loadToken());
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true); // true while we re-verify

  // Re-verify the token on mount (and whenever it changes)
  useEffect(() => {
    let cancelled = false;
    const verify = async () => {
      if (!token) {
        if (!cancelled) { setUser(null); setLoading(false); }
        return;
      }
      try {
        const res = await axios.get(`${API}/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setUser(res.data);
      } catch (e) {
        console.warn("[auth] token verify failed:", e);
        if (!cancelled) {
          setUser(null);
          setToken(null);
          saveToken(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    verify();
    return () => { cancelled = true; };
  }, [token]);

  const login = useCallback(async (email, password) => {
    try {
      const res = await axios.post(`${API}/login`, { email, password });
      saveToken(res.data.token);
      setToken(res.data.token);
      setUser(res.data.user);
      return { ok: true, user: res.data.user };
    } catch (e) {
      return { ok: false, error: formatDetail(e.response?.data?.detail) || e.message };
    }
  }, []);

  const register = useCallback(async (email, password, name) => {
    try {
      const res = await axios.post(`${API}/register`, { email, password, name });
      saveToken(res.data.token);
      setToken(res.data.token);
      setUser(res.data.user);
      return { ok: true, user: res.data.user };
    } catch (e) {
      return { ok: false, error: formatDetail(e.response?.data?.detail) || e.message };
    }
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    saveToken(null);
  }, []);

  const forgotPassword = useCallback(async (email) => {
    try {
      await axios.post(`${API}/forgot-password`, { email });
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatDetail(e.response?.data?.detail) || e.message };
    }
  }, []);

  const resetPassword = useCallback(async (resetToken, password) => {
    try {
      const res = await axios.post(`${API}/reset-password`, { token: resetToken, password });
      saveToken(res.data.token);
      setToken(res.data.token);
      setUser(res.data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatDetail(e.response?.data?.detail) || e.message };
    }
  }, []);

  const authHeader = useMemo(
    () => (token ? { Authorization: `Bearer ${token}` } : {}),
    [token],
  );

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!user,
    isAdmin: user?.role === "admin",
    login,
    register,
    logout,
    forgotPassword,
    resetPassword,
    authHeader,
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
};
